import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createFreshState, createInitialDraft } from '../utils/constants';
import { getProgressPercent, getResumeStep } from '../utils/stepNavigation';
import { useWizardSession } from './useWizardSession';
import { useWizardNavigation } from './useWizardNavigation';

const WizardContext = createContext(null);

export function WizardProvider({ children }) {
  const [, setSearchParams] = useSearchParams();

  const [sessionId, setSessionIdState] = useState(null);
  const [organisationId, setOrganisationId] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [currentStep, setCurrentStep] = useState(1);
  const [steps, setSteps] = useState(createFreshState().steps);
  const [draft, setDraftState] = useState(createInitialDraft());
  const [stepErrors, setStepErrors] = useState({});
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const sessionIdRef = useRef(null);
  const draftRef = useRef(draft);
  const currentStepRef = useRef(currentStep);
  const stepsRef = useRef(steps);

  sessionIdRef.current = sessionId;
  draftRef.current = draft;
  currentStepRef.current = currentStep;
  stepsRef.current = steps;

  const resumeStep = useMemo(() => getResumeStep(steps), [steps]);
  const progressPercent = useMemo(() => getProgressPercent(steps), [steps]);

  const syncUrl = useCallback(
    (step) => {
      setSearchParams({ step: String(step) }, { replace: true });
    },
    [setSearchParams]
  );

  const {
    hydrateFromSession,
    flushStepDraft,
    setDraft,
    syncSessionAfterMutation,
    resetWizardSession,
    clearCurrentStep,
    clearSaveTimeout,
    clearSessionId,
  } = useWizardSession({
    sessionIdRef,
    draftRef,
    currentStepRef,
    stepsRef,
    setSessionIdState,
    setOrganisationId,
    setProfileId,
    setCurrentStep,
    setSteps,
    setDraftState,
    setSaveError,
    setIsInitializing,
    setShowResumeBanner,
    bannerDismissed,
  });

  const {
    goToStep,
    validateCurrentStep,
    createProfileNow,
    handleContinue,
    handleGoLive,
    handleBack,
    handleSkip,
    handleClearStep,
    handleStartOver,
  } = useWizardNavigation({
    sessionIdRef,
    draftRef,
    currentStepRef,
    currentStep,
    steps,
    draft,
    profileId,
    organisationId,
    setCurrentStep,
    setStepErrors,
    setSaveError,
    setIsSaving,
    setShowResumeBanner,
    syncUrl,
    flushStepDraft,
    hydrateFromSession,
    syncSessionAfterMutation,
    resetWizardSession,
    clearCurrentStep,
    clearSaveTimeout,
    clearSessionId,
  });

  const dismissResumeBanner = useCallback(() => {
    setBannerDismissed(true);
    setShowResumeBanner(false);
  }, []);

  const value = useMemo(
    () => ({
      sessionId,
      organisationId,
      profileId,
      isInitializing,
      isSaving,
      saveError,
      currentStep,
      steps,
      draft,
      progressPercent,
      resumeStep,
      showResumeBanner,
      setDraft,
      goToStep,
      handleContinue,
      handleBack,
      handleSkip,
      handleClearStep,
      handleStartOver,
      handleGoLive,
      dismissResumeBanner,
      validateCurrentStep,
      createProfileNow,
      stepErrors,
    }),
    [
      sessionId,
      organisationId,
      profileId,
      isInitializing,
      isSaving,
      saveError,
      currentStep,
      steps,
      draft,
      progressPercent,
      resumeStep,
      showResumeBanner,
      setDraft,
      goToStep,
      handleContinue,
      handleBack,
      handleSkip,
      handleClearStep,
      handleStartOver,
      handleGoLive,
      dismissResumeBanner,
      validateCurrentStep,
      createProfileNow,
      stepErrors,
    ]
  );

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

export function useWizard() {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error('useWizard must be used within WizardProvider');
  return ctx;
}

/** @deprecated Use useWizard */
export function useOnboarding() {
  return useWizard();
}
